import React, { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import {
    Box,
    Button,
    CircularProgress,
    makeStyles,
    Paper,
    Theme,
    Typography,
} from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import BusinessIcon from '@material-ui/icons/Business'
import { useVendorW9Upload } from '../../hooks/useVendorW9Upload'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
    },
    card: {
        maxWidth: 560,
        width: '100%',
        padding: theme.spacing(4),
    },
    title: { fontWeight: 700, marginBottom: theme.spacing(1) },
    subtitle: {
        marginBottom: theme.spacing(3),
        color: theme.palette.text.secondary,
    },
    dropZone: {
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        padding: theme.spacing(5),
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: theme.palette.primary.main },
    },
    successBox: {
        textAlign: 'center',
        '& svg': { fontSize: 64, color: theme.palette.success.main },
    },
    errorBox: {
        textAlign: 'center',
        '& svg': { fontSize: 64, color: theme.palette.error.main },
    },
}))

export const VendorW9UploadPage: React.FC = () => {
    const classes = useStyles()
    const location = useLocation()
    const token = new URLSearchParams(location.search).get('token')
    const {
        info,
        isLoadingInfo,
        infoError,
        fetchInfo,
        upload,
        isUploading,
        uploadResult,
        uploadError,
    } = useVendorW9Upload(token)

    useEffect(() => {
        fetchInfo()
    }, [token])

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault()
            const file = e.dataTransfer.files[0]
            if (file) upload(file)
        },
        [upload],
    )

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (file) upload(file)
        },
        [upload],
    )

    if (isLoadingInfo) {
        return (
            <Box className={classes.root}>
                <CircularProgress />
            </Box>
        )
    }

    if (infoError) {
        return (
            <Box className={classes.root}>
                <Paper className={classes.card}>
                    <Box className={classes.errorBox}>
                        <ErrorIcon />
                        <Typography variant="h6">{infoError}</Typography>
                    </Box>
                </Paper>
            </Box>
        )
    }

    if (uploadResult) {
        return (
            <Box className={classes.root}>
                <Paper className={classes.card}>
                    <Box className={classes.successBox}>
                        <CheckCircleIcon />
                        <Typography variant="h6">
                            {uploadResult.message}
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Paper className={classes.card}>
                <Typography variant="h5" className={classes.title}>
                    Upload Your W-9
                </Typography>
                {info && (
                    <Box display="flex" alignItems="center" mb={2}>
                        <BusinessIcon
                            style={{ marginRight: 8 }}
                            color="action"
                        />
                        <Typography variant="body2" color="textSecondary">
                            Requested by{' '}
                            {info.apartment_names &&
                            info.apartment_names.length > 1
                                ? info.apartment_names.join(', ')
                                : info.apartment_name}
                        </Typography>
                    </Box>
                )}
                <Typography className={classes.subtitle}>
                    {info?.apartment_names && info.apartment_names.length > 1
                        ? 'This W-9 will be filed with each property listed above.'
                        : 'Please upload your completed W-9 form below.'}
                </Typography>

                {uploadError && (
                    <Typography color="error" style={{ marginBottom: 16 }}>
                        {uploadError}
                    </Typography>
                )}

                <Box
                    className={classes.dropZone}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                >
                    {isUploading ? (
                        <CircularProgress />
                    ) : (
                        <>
                            <CloudUploadIcon
                                style={{ fontSize: 48 }}
                                color="action"
                            />
                            <Typography variant="body1">
                                Drag &amp; drop your W-9 here
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginBottom: 16 }}
                            >
                                or
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                component="label"
                            >
                                Browse Files
                                <input
                                    type="file"
                                    hidden
                                    accept=".pdf,.png,.jpg,.jpeg"
                                    onChange={handleFileChange}
                                />
                            </Button>
                        </>
                    )}
                </Box>
            </Paper>
        </Box>
    )
}

export default VendorW9UploadPage
